/**
 * Map Index: curated map and data resources in sections, with tutorial-style activities (why, steps, outcome).
 * Optional location (from address lookup) is included so the binder has coordinates to use in any map.
 */

/** Display order and labels for sections. */
export const MAP_SECTIONS = [
  { id: 'general', title: 'General reference maps', description: 'Quick lookups: roads, terrain, and rail. Use your coordinates or address in each site’s search.' },
  { id: 'learn', title: 'Learn: Google Earth & tutorials', description: 'Step-by-step videos to use Google Earth for site assessment and planning.' },
  { id: 'elevation', title: 'Elevation & contour maps', description: 'Understand terrain, slopes, and drainage. Helps with flood risk, building placement, and water flow.' },
  { id: 'water', title: 'Water & watershed data', description: 'Streams, watersheds, and water-quality context. See what’s upstream and how water moves.' },
  { id: 'soil', title: 'Soil data', description: 'Soil types and properties for gardening, building, and drainage planning.' },
];

/**
 * Curated resources with section and activity (why, steps, outcome).
 * Each activity is a short tutorial: why do this, steps to follow, what you get.
 */
export const MAP_RESOURCES = [
  // ——— General reference maps ———
  {
    id: 'opentopomap',
    section: 'general',
    name: 'OpenTopoMap',
    url: 'https://opentopomap.org/',
    description: 'Topographic map (terrain, contours, trails).',
    activity: {
      why: 'See elevation, landform, and trails in one view. Useful for evacuation routes and understanding the lay of the land.',
      steps: ['Open the link.', 'In the search box, enter your address or paste your coordinates (lat, lon).', 'Zoom in or out to see neighborhood (1–2 mi) up to regional (10–20 mi) scale.'],
      outcome: 'A clear topo view of your area so you can plan routes and understand terrain.',
    },
  },
  {
    id: 'openrailwaymap',
    section: 'general',
    name: 'OpenRailwayMap',
    url: 'https://www.openrailwaymap.org/',
    description: 'Railway infrastructure (tracks, stations).',
    activity: {
      why: 'Know where rail lines and crossings are. Helps with evacuation routes, noise, and avoiding blocked roads.',
      steps: ['Open the link.', 'Search or pan to your area (use your coordinates in another map to find the general spot, then pan here).', 'Use the layer controls to show or hide rail types (e.g. active, abandoned).'],
      outcome: 'A map of rail infrastructure so you can plan around tracks and crossings.',
    },
  },
  {
    id: 'usgs',
    section: 'general',
    name: 'USGS National Map Viewer',
    url: 'https://apps.nationalmap.gov/viewer/',
    description: 'USGS topo, aerial imagery, and other layers.',
    activity: {
      why: 'Official US topo and aerial imagery. Good for property context and cross-checking with other maps.',
      steps: ['Open the link.', 'Use the search tool to enter your address or coordinates.', 'Switch layers (topo, imagery) from the layer list to compare views.'],
      outcome: 'Authoritative topo and aerial views of your location.',
    },
  },
  {
    id: 'osm',
    section: 'general',
    name: 'OpenStreetMap',
    url: 'https://www.openstreetmap.org/',
    description: 'General reference (roads, places, points of interest).',
    activity: {
      why: 'Detailed roads, names, and POIs. Useful for navigation and finding nearby services.',
      steps: ['Open the link.', 'Search for your address in the search box.', 'Zoom in for street detail or out for area overview.'],
      outcome: 'A familiar street map you can use for directions and reference.',
    },
  },
  // ——— Elevation & contour maps ———
  {
    id: 'earthexplorer',
    section: 'elevation',
    name: 'USGS EarthExplorer',
    url: 'https://earthexplorer.usgs.gov/',
    description: 'Search and download satellite and aerial imagery and elevation data.',
    activity: {
      why: 'Get official imagery and elevation datasets for your area. You can download data for offline or print use.',
      steps: ['Open the link.', 'Under “Search Criteria”, enter your address or coordinates to set your area of interest.', 'Open “Data Sets” and choose what you need (e.g. Aerial Imagery, Digital Elevation).', 'Set a date range if needed, then click “Results” to view or order.'],
      outcome: 'Access to download imagery and elevation data for your location.',
    },
  },
  {
    id: 'contourmapcreator',
    section: 'elevation',
    name: 'Contour Map Creator',
    url: 'https://contourmapcreator.urgr8.ch/',
    description: 'Free contour maps; export SVG or KML.',
    activity: {
      why: 'See elevation contours for a custom area. Helps with drainage, building placement, and understanding slopes.',
      steps: ['Open the link and go to your area on the map.', 'Set two markers (click the map) to define a rectangle, or enter North-West and South-East coordinates.', 'Click “get data” to fetch elevation and draw contours.', 'Adjust “Number of levels” or “Level interval” if needed.', 'Use “Save data” or “Download SVG file” / “Download KML file” to keep the map.'],
      outcome: 'A contour map of your chosen area you can save or overlay in Google Earth.',
    },
  },
  {
    id: 'contourmapgenerator',
    section: 'elevation',
    name: 'Contour Map Generator (5th World)',
    url: 'https://contourmapgenerator.com/',
    description: 'Free basic contour map; optional paid add-ons (water, sun, land, climate).',
    activity: {
      why: 'Get a ready-made contour map (and optional layers) for property planning. Good for stormwater and solar planning.',
      steps: ['Open the link and click “Get Started”.', 'Enter your location or draw your property boundary on the map.', 'Request the free Basic Contour Map (and add Water, Sun, or other layers if you want).', 'Receive the map by email; open in Google Earth or use the files as provided.'],
      outcome: 'A contour map (and optional reports) tailored to your property for planning.',
    },
  },
  // ——— Water & watershed ———
  {
    id: 'epa-waters',
    section: 'water',
    name: 'EPA WATERS Data (Google Earth)',
    url: 'https://www.epa.gov/waterdata/viewing-waters-data-using-google-earth',
    description: 'Water bodies, watersheds, and EPA water data in Google Earth.',
    activity: {
      why: 'See streams, catchments, and EPA water-program data (e.g. impairments, monitoring) in one place. Helps understand flood risk and water quality context.',
      steps: ['Download the WATERS KMZ file from the EPA page (requires Google Earth 7.3 or newer).', 'Open the KMZ file in Google Earth.', 'In the Layers panel, turn on the water and watershed layers you want.', 'Search or fly to your address or coordinates in Google Earth.', 'Click features on the map for details (stream names, watershed info, links to reports).'],
      outcome: 'A Google Earth view of streams and watersheds with linked EPA data for your area.',
    },
  },
  // ——— Soil ———
  {
    id: 'soilweb',
    section: 'soil',
    name: 'SoilWeb Apps (UC Davis)',
    url: 'https://casoilresource.lawr.ucdavis.edu/soilweb-apps',
    description: 'USDA soil survey (SSURGO) for most of the US.',
    activity: {
      why: 'Know your soil type, drainage, and properties. Informs gardening, foundations, septic, and drainage decisions.',
      steps: ['Open the link and choose “SoilWeb” (browser map) or “SoilWeb Earth” (KML for Google Earth).', 'Search or pan to your location (address or coordinates).', 'Click a map unit on the map to see soil name, texture, drainage class, and other properties.', 'Use “Soil Series Extent Explorer” or “Soil Series Data Explorer” for more detail on a specific soil type.'],
      outcome: 'Soil map and properties for your location to guide planting and building.',
    },
  },
  // ——— Learn: tutorials ———
  {
    id: 'ge-homestead-1',
    section: 'learn',
    name: 'Google Earth for Homesteading (video)',
    url: 'https://www.youtube.com/watch?v=iAlTg08loG4',
    description: 'YouTube tutorial on using Google Earth for homestead and property planning.',
    activity: {
      why: 'Learn how to use Google Earth for site assessment, measuring, and overlaying data. Builds skills you can use with the other resources.',
      steps: ['Watch the video (bookmark or open from the link below).', 'Open Google Earth (desktop or web) and search for your address or paste your coordinates.', 'Practice the techniques shown: measuring distance, saving places, and changing layers.'],
      outcome: 'Confidence using Google Earth to explore and assess your property and area.',
    },
  },
  {
    id: 'ge-homestead-2',
    section: 'learn',
    name: 'Google Earth Tutorial (video)',
    url: 'https://www.youtube.com/watch?v=pJZKYi-OEYg',
    description: 'YouTube tutorial on Google Earth for land and preparedness use.',
    activity: {
      why: 'Another step-by-step guide to Google Earth. Reinforces skills and may show features you didn’t know about.',
      steps: ['Watch the video from the link below.', 'Follow along in Google Earth with your own location (address or coordinates).', 'Try measuring areas, viewing historical imagery if available, and saving a placemark for your property.'],
      outcome: 'Better use of Google Earth for land and preparedness planning.',
    },
  },
];

function escapeHtml(text) {
  if (text == null || text === '') return '';
  const s = String(text);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build binder section HTML for Map Index.
 * Groups resources by section and renders each as a tutorial: Why, Steps, What you get, plus link.
 */
export function buildMapIndexSectionHtml(data) {
  let html = '<div class="section-content">';
  html += '<div class="section-title">Map Index</div>';
  html += '<p class="section-description">This page is your one-place reference for free map and data tools. It has (1) your saved location and coordinates, and (2) step-by-step activities by topic. When you have connectivity, open each link below and use your coordinates—or type your address—where the site asks for a location. Follow the steps to get maps and data for your area.</p>';

  const hasCoords = typeof data?.lat === 'number' && typeof data?.lon === 'number';
  if (hasCoords) {
    const displayName = escapeHtml(data.displayName || data.address || '');
    const locationLine = displayName || 'Your location';
    html += '<div class="subsection-title">Your location</div>';
    html += `<div class="item-row"><span class="label">Location:</span> ${locationLine}</div>`;
    html += `<div class="item-row"><span class="label">Coordinates:</span> ${Number(data.lat).toFixed(5)}, ${Number(data.lon).toFixed(5)}</div>`;
    html += '<div class="item-row" style="margin-bottom: 8px; padding: 8px 10px; background: #f5f5f5; border-left: 3px solid #333; font-size: 0.9em;"><strong>Use these coordinates in every resource below.</strong> When a site asks for a location or search, paste the coordinates above (or type your address).</div>';
  } else {
    html += '<div class="item-row" style="color: #666; margin-bottom: 14px;">Add your location in the Map Index tool to print your coordinates here. The resources below still work—you’ll type your address or coordinates in each site when you open it.</div>';
  }

  html += '<div class="subsection-title" style="margin-top: 16px;">Resources by topic</div>';
  html += '<p class="section-description" style="margin-top: 0; margin-bottom: 12px;">Each link has a short activity: <strong>Why</strong> to use it, <strong>Steps</strong> to follow, and <strong>What you get</strong>. Use your coordinates (or address) in each site’s search or location field.</p>';

  MAP_SECTIONS.forEach((sec) => {
    const resourcesInSection = MAP_RESOURCES.filter((r) => r.section === sec.id);
    if (resourcesInSection.length === 0) return;
    const title = escapeHtml(sec.title);
    const desc = escapeHtml(sec.description);
    html += `<div class="subsection-title">${title}</div>`;
    html += `<p class="section-description" style="margin-top: 0; margin-bottom: 10px;">${desc}</p>`;

    resourcesInSection.forEach((res) => {
      const name = escapeHtml(res.name);
      const url = escapeHtml(res.url);
      const activity = res.activity;
      if (!activity) return;
      const why = escapeHtml(activity.why);
      const outcome = escapeHtml(activity.outcome);
      html += '<div class="item-row" style="margin-left: 10px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #eee;">';
      html += `<strong>${name}</strong><br/>`;
      html += `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a><br/>`;
      html += `<span class="label" style="display: block; margin-top: 6px;">Why:</span> <span style="font-size: 0.95em;">${why}</span><br/>`;
      html += '<span class="label" style="display: block; margin-top: 4px;">Steps:</span><ol style="margin: 4px 0 8px 18px; font-size: 0.95em;">';
      (activity.steps || []).forEach((step) => {
        html += `<li>${escapeHtml(step)}</li>`;
      });
      html += '</ol>';
      html += `<span class="label">What you get:</span> <span style="font-size: 0.95em;">${outcome}</span>`;
      html += '</div>';
    });
  });

  html += '</div>';
  return html;
}
